import { DestroyRef, Injector, type Provider } from '@angular/core';
import type { ISettingsRepository } from '@app/domain/ports';
import {
  CountAccountTransfersUseCase,
  CountExpensesUseCase,
  CountIncomesUseCase,
  GetAccountBalanceUseCase,
  GetAccountsUseCase,
  GetAccountTransfersUseCase,
  GetCategoriesUseCase,
  GetCurrenciesUseCase,
  GetCurrencyExchangesUseCase,
  GetExpensesUseCase,
  GetIncomesUseCase,
  GetTagsUseCase,
} from '@app/domain/use-cases';
import { SETTINGS_REPOSITORY } from '@app/platform/tokens';
import { DEVELOPER_SETTINGS_PLUGINS, IDLE_CONTROL_PORT, PLUGIN_TRANSLATION_ASSETS, type IdleControlPort } from '@app/ui/shared';
import { AiSessionCoordinator } from './ai-session-coordinator';
import { AiSessionRelayClient } from './ai-session-relay.client';
import { AiSessionSettingsService } from './ai-session-settings.service';
import { FinanceToolRegistry } from './finance-tool.registry';
import { AI_SESSION_HOST, type AiSessionHostPort } from './tokens';
import { AiSessionSettingComponent } from './ui/ai-session-setting.component';

export function provideAiSessionPlugin(): Provider[] {
  return [
    {
      provide: AiSessionSettingsService,
      useFactory: (settings: ISettingsRepository) => new AiSessionSettingsService(settings),
      deps: [SETTINGS_REPOSITORY],
    },
    AiSessionRelayClient,
    {
      provide: FinanceToolRegistry,
      useFactory: (
        getAccounts: GetAccountsUseCase,
        getAccountBalance: GetAccountBalanceUseCase,
        getCategories: GetCategoriesUseCase,
        getCurrencies: GetCurrenciesUseCase,
        getTags: GetTagsUseCase,
        getExpenses: GetExpensesUseCase,
        countExpenses: CountExpensesUseCase,
        getIncomes: GetIncomesUseCase,
        countIncomes: CountIncomesUseCase,
        getTransfers: GetAccountTransfersUseCase,
        countTransfers: CountAccountTransfersUseCase,
        getExchanges: GetCurrencyExchangesUseCase,
      ) =>
        new FinanceToolRegistry(
          getAccounts,
          getAccountBalance,
          getCategories,
          getCurrencies,
          getTags,
          getExpenses,
          countExpenses,
          getIncomes,
          countIncomes,
          getTransfers,
          countTransfers,
          getExchanges,
        ),
      deps: [
        GetAccountsUseCase,
        GetAccountBalanceUseCase,
        GetCategoriesUseCase,
        GetCurrenciesUseCase,
        GetTagsUseCase,
        GetExpensesUseCase,
        CountExpensesUseCase,
        GetIncomesUseCase,
        CountIncomesUseCase,
        GetAccountTransfersUseCase,
        CountAccountTransfersUseCase,
        GetCurrencyExchangesUseCase,
      ],
    },
    {
      provide: AiSessionCoordinator,
      useFactory: (
        host: AiSessionHostPort,
        settings: AiSessionSettingsService,
        relay: AiSessionRelayClient,
        tools: FinanceToolRegistry,
        injector: Injector,
        destroyRef: DestroyRef,
        idle: IdleControlPort,
      ) => new AiSessionCoordinator(host, settings, relay, tools, injector, destroyRef, idle),
      deps: [
        AI_SESSION_HOST,
        AiSessionSettingsService,
        AiSessionRelayClient,
        FinanceToolRegistry,
        Injector,
        DestroyRef,
        IDLE_CONTROL_PORT,
      ],
    },
    {
      provide: PLUGIN_TRANSLATION_ASSETS,
      useValue: {
        pluginId: 'ai-session',
        basePath: './assets/i18n/plugins/ai-session',
        locales: ['en', 'pl', 'ru', 'uk'],
      },
      multi: true,
    },
    {
      provide: DEVELOPER_SETTINGS_PLUGINS,
      useValue: { component: AiSessionSettingComponent },
      multi: true,
    },
  ];
}
